import { createSelector } from 'reselect';
import createCachedSelector, { LruCacheObject } from 're-reselect';
import * as transactionSelectors from 'data/transactions/selectors';
import * as payeeSelectors from 'data/payees/selectors';
import { getIncomeCOAIds } from 'data/categories/selectors';
import { recurrenceFromFrequencyAlias, frequencyAliasTypes } from 'data/scheduledTransactions/scheduledTransactionsUtils';
import { RRule } from 'rrule';
import { DateTime, Interval } from 'luxon';
import { mkScheduledTransaction, STTypeEnum } from 'data/scheduledTransactions/scheduledTransactionsTypes';
import { v4 as uuidv4 } from 'uuid';
import { getLogger } from 'utils/logger';
import { List as ListImmutable, Set, Map as MapImmutable } from 'immutable';
import { isExpenseCat, isIncomeCat } from 'data/categories/retrievers';
import { CoaTypeEnum } from 'data/chartOfAccounts/types';
import { isSplitTxn, isIncomeFromTxn } from 'data/transactions/utils';
import { getAccountsForInstitutionLoginId } from 'data/accounts/selectors';

import { SCHEDULED_TRANSACTIONS_REDUCER_KEY } from './scheduledTransactionsReducer';

const log = getLogger('components/QDialogs/index.js');

export const getScheduledTransactionsStore = (state) => state[SCHEDULED_TRANSACTIONS_REDUCER_KEY];
export const getLoadPending = (state) => getScheduledTransactionsStore(state).loadPending;
export const getError = (state) => getScheduledTransactionsStore(state).error;
export const isLoading = (state) => getScheduledTransactionsStore(state).isLoading;
export const getLastSyncDate = (state) => getScheduledTransactionsStore(state).lastSyncDate;
export const getScheduledTransactions = (state) => getScheduledTransactionsStore(state).resourcesById;
export const getScheduledTransactionById = (state, id) => getScheduledTransactions(state).get(id);
export const getScheduledTransactionByClientId = (state, clientId) => getScheduledTransactions(state).find((scheduledTransaction) => scheduledTransaction.clientId === clientId);

export const getScheduledTransactionsSortedByPayee = createSelector(
  getScheduledTransactions,
  (scheduledTransactions) => !scheduledTransactions ? null :
    scheduledTransactions.sort((a,b) => (
      a.transaction.payee.localeCompare(
        b.transaction.payee,
        undefined,
        { numeric: true, sensitivity: 'base' },
      )
    ))
);

export const getUserVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById) => scheduledTransactionsById.filter((scheduledTransaction) => scheduledTransaction.isUserVerified === undefined || scheduledTransaction.isUserVerified)
);

export const getNonVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById) => scheduledTransactionsById.filter((scheduledTransaction) => scheduledTransaction.isUserVerified === false || !scheduledTransaction.isUserVerified)
);


export const getBillsAndIncome = createSelector(
  getScheduledTransactions,
  getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) => ({
    bills: scheduledTransactions.filter((model) => !isIncomeFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
    income: scheduledTransactions.filter((model) => isIncomeFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
  })
);
export const getBillsAndIncomeUserVerified = createSelector(
  getUserVerifiedScheduledTransactions,
  getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) => (new MapImmutable ({
    bills: scheduledTransactions.filter((model) => !isIncomeFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
    income: scheduledTransactions.filter((model) => isIncomeFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
  }))
);

export const getAccountFilteredBillsAndIncome = createSelector(
  getAccountsForInstitutionLoginId,
  getScheduledTransactions,
  getIncomeCOAIds,
  (accounts, scheduledTransactions, incomeCOAs) => {
    const accountIds = accounts.map((account) => account.id);
    const accountSchedules = scheduledTransactions.filter((model) => accountIds.includes(model.transaction.accountId));
    return ({
      bills: accountSchedules.filter((model) => !isIncomeFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
      income: accountSchedules.filter((model) => isIncomeFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
    })
  }
);





// ---------------------------------------------------------------------------------------------------------------
// #                                         Outdated Selectors                                                  #
// #                                                                                                             #
// #                             Haven't been used since bill scout creation.                                    #
// #              (personally I think there's some cool logic in here we might want to revive)                   #
// ---------------------------------------------------------------------------------------------------------------

// currently unused
export const proposedScheduledTransactionForTransaction = createSelector(
  (state, transaction) => transactionSelectors.getTransactionsByFilter(state, {
    accountIdsArray: [transaction.accountId],
    payee: transaction.payee,
    isScheduled: false,
  }),
  (state, transaction) => transaction,
  (transactions, transactionPre) => {

    // do not allow split scheduled transactions, yet
    let transaction = transactionPre;
    if (transaction && isSplitTxn(transaction)) {
      transaction = transaction.set('coa', transaction.split.items.first().coa).set('split', null);
    }

    let proposedScheduledTransaction = scheduledTransactionFromTransaction(transactions, transaction);
    if (!proposedScheduledTransaction) { // set defaults
      const type = (transaction.coa.type === CoaTypeEnum.UNCATEGORIZED
        || (transaction.coa.type === CoaTypeEnum.CATEGORY && isExpenseCat(transaction.coa.id))) ? STTypeEnum.BILL : STTypeEnum.UNKNOWN;
      let dueOn;
      let frequency;
      const transactionDate = DateTime.fromISO(transaction.postedOn);
      if (transaction.coa.type === CoaTypeEnum.CATEGORY && isIncomeCat(transaction.coa.id)) {
        frequency = frequencyAliasTypes.everyTwoWeeksACME;
        const recurrence = recurrenceFromFrequencyAlias(frequency, transaction.postedOn);
        if (recurrence) {
          const rule = new RRule({
            freq: RRule.WEEKLY,
            interval: recurrence.interval,
            byweekday: recurrence && recurrence.byDay && recurrence.byDay.map((weekday) => weekDayMap[weekday]),
            dtstart: transactionDate.toJSDate(),
            until: DateTime.local().plus({ weeks: 2 }).toJSDate(),
          });
          const dueOnDate = rule.after( new Date(), false);
          dueOn = dueOnDate && DateTime.fromJSDate(dueOnDate).toISODate();
        }
      } else {
        frequency = frequencyAliasTypes.everyMonthACME;
        const recurrence = recurrenceFromFrequencyAlias(frequency, transaction.postedOn);
        const rule = new RRule({
          freq: RRule.MONTHLY,
          interval: recurrence.interval,
          bymonthday: recurrence.byMonthDay,
          dtstart: transactionDate.toJSDate(),
          until: DateTime.local().plus({ months: 1 }).toJSDate(),
        });
        const dueOnDate = rule.after( new Date(), false);
        dueOn = dueOnDate && DateTime.fromJSDate(dueOnDate).toISODate();
      }

      const recurrence = recurrenceFromFrequencyAlias(frequency, dueOn);
      proposedScheduledTransaction = mkScheduledTransaction({
        clientId: uuidv4().toUpperCase(),
        dueOn,
        type,
        recurrence,
        transaction,
      });
    }
    return proposedScheduledTransaction;
  },
);

export const bucketizeTransactionsByAccountId = createCachedSelector(
  (transactionsById) => transactionsById,
  (transactionsById) => MapImmutable().withMutations(
    (transactionsByAccountIdMutable) => transactionsById && transactionsById.forEach(
      (transaction) => transactionsByAccountIdMutable.set(
        transaction.accountId,
        (transactionsByAccountIdMutable.get(transaction.accountId) || ListImmutable()).push(transaction)
      )
    )
  ),
)(
  (transactionsById) => transactionsById && transactionsById.hashCode(),
  { cacheObject: new LruCacheObject({ cacheSize: 6 }) },
);

// Only used in setupScheduledTransactionsField, which currently is unused
export const proposedScheduledTransactions = createCachedSelector(
  getScheduledTransactions,
  (state, props) => props.transactions || transactionSelectors.getTransactionsById(state),
  (state, props) => props.payees || payeeSelectors.getPayeesForTransactions(state, props.transactions),
  (
    scheduledTransactionsById,
    transactionsById,
    payees,
  ) => {
    const scheduledTransactions = [];

    const transactionsByAccountId = bucketizeTransactionsByAccountId(transactionsById);
    transactionsByAccountId.forEach((accountTransactions, accountId) => {
      const scheduledPayees = scheduledTransactionsById
        .filter((scheduledTransaction) => scheduledTransaction.transaction.accountId === accountId && scheduledTransaction.transaction.payee)
        .reduce((payeesAccumulator, scheduledTransaction) => payeesAccumulator.add(scheduledTransaction.transaction.payee.toLowerCase()), new Set());
      const transactionsByPayee = new Map();
      accountTransactions
        .filter((transaction) => !transaction.stModelId && transaction.payee)
        .forEach((transaction) => {
          const payee = transaction.payee.toLowerCase();
          if (payees.includes(payee) && !scheduledPayees.includes(payee)) {
            const payeeBucket = transactionsByPayee.get(payee) || [];
            payeeBucket.push(transaction);
            transactionsByPayee.set(payee, payeeBucket);
          }
        });

      transactionsByPayee.forEach((transactions) => {
        const selectedTransaction = transactions.reduce((latest, transaction) => transaction.postedOn > latest.postedOn ? transaction : latest);
        const scheduledTransaction = scheduledTransactionFromTransaction(ListImmutable(transactions), selectedTransaction);
        if (scheduledTransaction) {
          scheduledTransactions.push(scheduledTransaction);
        }
      });
    });

    return scheduledTransactions;
  },
)(
  (state, props) => `${props.transactions && props.transactions.hashCode()}:${props.payees && props.payees.hashCode()}`,
  { cacheObject: new LruCacheObject({ cacheSize: 6 }) },
);

// Unused because the above is unused
function scheduledTransactionFromTransaction(transactions, selectedTransaction) {
  let scheduledTransaction;

  const minInstancesToMatch = 3;
  const offsetDays = 3;
  if (transactions.size >= minInstancesToMatch) {
    const recurrenceTemplates = new RecurrenceTemplates(selectedTransaction, minInstancesToMatch);
    let bestRecurrenceTemplate = {
      score: -minInstancesToMatch * offsetDays * minInstancesToMatch,
      recurrenceTemplate: undefined,
    };
    // eslint-disable-next-line no-restricted-syntax
    for (const recurrenceTemplate of recurrenceTemplates) {
      const recurrenceTemplateScore = recurrenceTemplate.dates.reduce((templateScore, templateDate) => {
        const transactionMatched = transactions.reduce((bestTransactionMatched, transaction) => {
          let resultTransactionMatched = bestTransactionMatched;
          const transactionDate = DateTime.fromISO(transaction.postedOn);
          const interval = templateDate < transactionDate ? Interval.fromDateTimes(templateDate, transactionDate) : Interval.fromDateTimes(transactionDate, templateDate);
          const offset = interval.length('days');
          if (offset <= bestTransactionMatched.offsetDays) {
            resultTransactionMatched = {
              transaction,
              offsetDays: offset,
            };
          }
          return resultTransactionMatched;
        }, {
          transaction: undefined,
          offsetDays,
        });
        return templateScore + (transactionMatched.transaction ? offsetDays - transactionMatched.offsetDays : -minInstancesToMatch * offsetDays);
      }, 0);
      if (bestRecurrenceTemplate.score <= recurrenceTemplateScore) {
        bestRecurrenceTemplate = {
          score: recurrenceTemplateScore,
          recurrenceTemplate,
        };
      }
    }

    if (bestRecurrenceTemplate.recurrenceTemplate && bestRecurrenceTemplate.recurrenceTemplate.frequency && bestRecurrenceTemplate.recurrenceTemplate.rule) {
      const dueOnDate = bestRecurrenceTemplate.recurrenceTemplate.rule.after(new Date(), false);
      const dueOn = dueOnDate && DateTime.fromJSDate(dueOnDate).toISODate();
      if (dueOn) {
        const recurrence = recurrenceFromFrequencyAlias(bestRecurrenceTemplate.recurrenceTemplate.frequency, dueOn);
        if (recurrence) {
          let type;
          if (selectedTransaction.coa) {
            type = (selectedTransaction.coa.type === CoaTypeEnum.UNCATEGORIZED
              || (selectedTransaction.coa.type === CoaTypeEnum.CATEGORY && isExpenseCat(selectedTransaction.coa.id))) ? STTypeEnum.BILL : STTypeEnum.UNKNOWN;
          } else {
            type = STTypeEnum.BILL;
          }
          scheduledTransaction = mkScheduledTransaction({
            clientId: uuidv4().toUpperCase(),
            dueOn,
            type,
            recurrence,
            transaction: selectedTransaction,
          });
        }
      }
    }
  }
  log.log('proposed scheduled transaction: ', scheduledTransaction);

  return scheduledTransaction;
}

const weekDayMap = {
  'MO': RRule.MO,
  'TU': RRule.TU,
  'WE': RRule.WE,
  'TH': RRule.TH,
  'FR': RRule.FR,
  'SA': RRule.SA,
  'SU': RRule.SU,
};
class RecurrenceTemplates {

  constructor(transaction, instancesNum) {
    this.instancesNum = instancesNum;
    this.transaction = transaction;
    this.transactionDate = DateTime.fromISO(transaction.postedOn, {zone: 'utc'})
  }

  *[Symbol.iterator]() {
    let recurrence;
    let frequency;

    frequency = frequencyAliasTypes.everyWeekACME;
    recurrence = recurrenceFromFrequencyAlias(frequency, this.transaction.postedOn);
    if (recurrence) {
      const rule = new RRule({
        freq: RRule.WEEKLY,
        interval: recurrence.interval,
        byweekday: recurrence && recurrence.byDay && recurrence.byDay.map((weekday) => weekDayMap[weekday]),
        dtstart: this.transactionDate.minus({ weeks: recurrence.interval * this.instancesNum }).toJSDate(),
        // until: this.transactionDate.plus({ weeks: recurrence.interval * this.instancesNum }).toJSDate(),
        until: DateTime.local().plus({ weeks: recurrence.interval * this.instancesNum }).toJSDate(),
      });
      const dates = rule.all().map((jsDate) => DateTime.fromJSDate(jsDate));
      if (dates.length > 2*this.instancesNum) {
        yield { frequency, dates, rule };
      }
    }

    frequency = frequencyAliasTypes.everyTwoWeeksACME;
    recurrence = recurrenceFromFrequencyAlias(frequency, this.transaction.postedOn);
    if (recurrence) {
      const rule = new RRule({
        freq: RRule.WEEKLY,
        interval: recurrence.interval,
        byweekday: recurrence && recurrence.byDay && recurrence.byDay.map((weekday) => weekDayMap[weekday]),
        dtstart: this.transactionDate.minus({ weeks: recurrence.interval * this.instancesNum }).toJSDate(),
        // until: this.transactionDate.plus({ weeks: recurrence.interval * this.instancesNum }).toJSDate(),
        until: DateTime.local().plus({ weeks: recurrence.interval * this.instancesNum }).toJSDate(),
      });
      const dates = rule.all().map((jsDate) => DateTime.fromJSDate(jsDate));
      if (dates.length > 2*this.instancesNum) {
        yield { frequency, dates, rule };
      }
    }

    frequency = frequencyAliasTypes.twiceAMonthACME;
    recurrence = recurrenceFromFrequencyAlias(frequency, this.transaction.postedOn);
    if (recurrence) {
      const rule = new RRule({
        freq: RRule.MONTHLY,
        interval: recurrence.interval,
        bymonthday: recurrence.byMonthDay,
        dtstart: this.transactionDate.minus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
        // until: this.transactionDate.plus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
        until: DateTime.local().plus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
      });
      const dates = rule.all().map((jsDate) => DateTime.fromJSDate(jsDate));
      if (dates.length > 2*this.instancesNum) {
        yield { frequency, dates, rule };
      }
    }

    frequency = frequencyAliasTypes.everyMonthACME;
    recurrence = recurrenceFromFrequencyAlias(frequency, this.transaction.postedOn);
    if (recurrence) {
      const rule = new RRule({
        freq: RRule.MONTHLY,
        interval: recurrence.interval,
        bymonthday: recurrence.byMonthDay,
        dtstart: this.transactionDate.minus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
        // until: this.transactionDate.plus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
        until: DateTime.local().plus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
      });
      const dates = rule.all().map((jsDate) => DateTime.fromJSDate(jsDate));
      if (dates.length > 2*this.instancesNum) {
        yield { frequency, dates, rule };
      }
    }

    frequency = frequencyAliasTypes.every6MonthsACME;
    recurrence = recurrenceFromFrequencyAlias(frequency, this.transaction.postedOn);
    if (recurrence) {
      const rule = new RRule({
        freq: RRule.MONTHLY,
        interval: recurrence.interval,
        bymonthday: recurrence.byMonthDay,
        dtstart: this.transactionDate.minus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
        // until: this.transactionDate.plus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
        until: DateTime.local().plus({ months: recurrence.interval * this.instancesNum }).toJSDate(),
      });
      const dates = rule.all().map((jsDate) => DateTime.fromJSDate(jsDate));
      if (dates.length > 2*this.instancesNum) {
        yield { frequency, dates, rule };
      }
    }

    frequency = frequencyAliasTypes.everyYearACME;
    recurrence = recurrenceFromFrequencyAlias(frequency, this.transaction.postedOn);
    if (recurrence) {
      const rule = new RRule({
        freq: RRule.YEARLY,
        interval: recurrence.interval,
        byyearday: recurrence.byYearDay,
        dtstart: this.transactionDate.minus({ years: recurrence.interval * this.instancesNum }).toJSDate(),
        // until: this.transactionDate.plus({ years: recurrence.interval * this.instancesNum }).toJSDate(),
        until: DateTime.local().plus({ years: recurrence.interval * this.instancesNum }).toJSDate(),
      });
      const dates = rule.all().map((jsDate) => DateTime.fromJSDate(jsDate));
      if (dates.length > 2*this.instancesNum) {
        yield { frequency, dates, rule };
      }
    }
  }
}
